import React, { useEffect, useState } from "react";
import { CustomerManagerInformationBox } from "../../../Components/Information/CustomerManagerInformationBox";
import { InitControlValueData } from "../../../Components/Navigation/CarFilterBar";
import { axiosInstance } from "../../../Api/AxiosConfig";
import { VATIcon } from "../../../Components/Cars/VATIcon";
import { Waiting } from "../../../Components/System/Waiting";
import { CarSearchBox } from "../../../Components/Navigation/CarSearchBox";
import { BrandPillSelection } from "../../../Components/Brand/BrandPillSelection";
import { CarFilterMobile } from "../../../Components/Navigation/CarFilterMobile";
import { CarSortMobile } from "../../../Components/Navigation/CarSortMobile";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid";
import { ShowroomMode } from "../../../Components/Navigation/ShowroomMode";
import { useLocation } from "react-router-dom";
import { useGlobalDataContext } from "../../../GlobalState/GlobalDataProvider";
import { getCookie } from "../../../Hooks/UseGenericFunctions";
import { MobileLayout } from "../../../Components/Layouts/MobileLayout";
import { MobileShowroomCarCard } from "./MobileShowroomCarCard";
import { CarzelleTooltip } from "../../../Components/System/Messages/CarzelleTooltip";
import { OverlayClose } from "../../../Components/Navigation/OverlayClose";
import { ExplanationModal } from "../../../Components/System/Messages/ExplanationModal";
import { UpdateDataPrompt } from "../../../Components/System/Messages/UpdateDataPrompt";
import { useDisclosure } from "@mantine/hooks";
import { useCarUpdateThrottle } from "../../../Hooks/useCarUpdateThrottle";

export const MobileView = () => {
    const [data, setData] = useState();
    const [makes, setMakes] = useState();
    const [updateData, setUpdateData] = useState(false);
    const [sortByField, setSortByField] = useState('designation');
    const [filterData, setFilterData] = useState(InitControlValueData);
    const [selectedCarId, setSelectedCarId] = useState(null);
    const [showFilterOptions, setShowFilterOptions] = useState(false);
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const carLink = searchParams.get("carLink");
    const [setShowSearch] = useState(carLink !== null);
    const { showroomMode, setShowroomMode, globalData } = useGlobalDataContext();
    const [equipmentIcons, setEquipmentIcons] = useState();
    const [opened, { open, close }] = useDisclosure(false);

    axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + getCookie('CARZELLE_API_TOKEN');

    // Use centralized car update throttling hook
    useCarUpdateThrottle(
        globalData?.carUpdateCounter,
        () => {
            setUpdateData(prev => !prev);
            open();
        },
        60000
    );

    const filterList = (data) => {
        let dataToFilter = data.filter((car) => car.buy_status !== 'listing');

        if (filterData.make_id !== null) {
            dataToFilter = dataToFilter.filter((car) => car.make_id.toString() === filterData.make_id);
        }

        if (filterData.in_basket) {
            dataToFilter = dataToFilter.filter((car) => car.in_basket !== null);
        }

        if (filterData.search_text !== '') {
            dataToFilter = dataToFilter.filter((car) => (car.designation + car.vin_no + car.car_id + car.info_line_1 + car.info_line_2).toLowerCase().includes(filterData.search_text.toLowerCase()));
        }

        if (filterData.hide_sold) {
            dataToFilter = dataToFilter.filter((car) => car.is_sold === null);
        }

        if (filterData.buy_status) {
            dataToFilter = dataToFilter.filter((car) => car.buy_status === 'fast_delivery');
        }

        if (sortByField !== 'price') {
            dataToFilter = dataToFilter.sort((a, b) => {
                if (a[sortByField] < b[sortByField]) {
                    return -1;
                } else if (a[sortByField] > b[sortByField]) {
                    return 1;
                } else {
                    // If the primary sortByField values are equal, fall back to sorting by car_id
                    return a.hash - b.hash;
                }
            });
        } else {
            dataToFilter = dataToFilter.sort((a, b) => {
                if (parseInt(a.price) < parseInt(b.price)) {
                    return -1;
                } else if (parseInt(a.price) > parseInt(b.price)) {
                    return 1;
                } else {
                    // If the prices are equal, fall back to sorting by hash
                    return a.hash - b.hash;
                }
            });
        }
        return dataToFilter;
    }

    const ScrollToCarCard = (carId) => {
        console.log("Scrolling to car with ID:", carId);
        const el = document.getElementById(`car-${carId}`);

        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }

    useEffect(() => {
        axiosInstance
            .get('/shop/buy-list')

            .then((response) => {
                setMakes(response.data.makes);
                setData(response.data.list);
                setEquipmentIcons(response.data.equipmentBase);

            })
            .catch((error) => {
                console.error("Error fetching data:", error);
            });
    }, [updateData]); // eslint-disable-line react-hooks/exhaustive-deps

    if (data?.length > 0) {
        return (
            <>
            <ExplanationModal 
                HelpComponent={<UpdateDataPrompt title="New data - click to update" />} 
                opened={updateData} 
                close={close} 
                title="Update Available" 
            />
            <MobileLayout showroomMode={showroomMode} enableSearch={false} showSearch={false} setShowSearch={setShowSearch} updateData={updateData} setUpdateData={setUpdateData}>
                <div className={`sticky top-[10px] h-[30px] left-0 z-[1000] pb-2 mb-2 bg-background-color`}>
                    <div className="ml-6 flex items-center mt-4 text-xs text-gray-500 cursor-pointer">
                        <div style={{ display: showFilterOptions ? 'none' : 'flex' }} onClick={() => setShowFilterOptions(true)}>
                            <div className="flex items-center mt-2">Filter and sort options <ChevronDownIcon className="w-3 ml-3" /></div>
                        </div>
                        <div style={{ display: showFilterOptions ? 'flex' : 'none' }} onClick={() => setShowFilterOptions(false)}>
                            <div className="flex items-center">Hide filter and sort options <ChevronUpIcon className="w-3 ml-3" /></div>
                        </div>
                    </div>

                    {showFilterOptions &&
                        <div className="relative z-[1000] bg-gray-100 p-4 mt-2">
                            <div className="absolute right-[-10px] top-[-50px]">
                                <OverlayClose close={() => setShowFilterOptions(false)} className="text-gray-500 p-1 top-4 right-4 z-[0] bg-gray-200" />
                            </div>
                            <div className="">
                                <CarSearchBox fnSearchValueChange={(filterValues) => setFilterData(filterValues)} />
                            </div>
                            <div className="flex items-center mt-4">
                                <CarzelleTooltip message="Show/hide prices" position="bottom" transition="pop" openDelay={1500}>
                                    <ShowroomMode value={showroomMode} changeValue={(value) => setShowroomMode(value)} />
                                </CarzelleTooltip>
                            </div>
                            <div className='flex items-center mt-4'>
                                <BrandPillSelection makes={makes} fnFilterChange={(filterValues) => setFilterData(filterValues)} />
                            </div>

                            <div className='flex items-center mt-2'>
                                <CarFilterMobile fnFilterChange={(filterValues) => setFilterData(filterValues)} />
                            </div>

                            <div className='flex items-center mt-4'>
                                <CarSortMobile fnSortChange={(value) => setSortByField(value)} activeSortIndex={1} />
                            </div>
                        </div>
                    }
                </div>
                <div className="px-2 pb-2 overscroll-contain touch-pan-y overflow-y-scroll h-screen">
                    <div>
                        {filterList(data).map((car) => (
                            <div key={`car-card-${car.hash}`} id={`car-${car.hash}`} className="mt-4">
                                <MobileShowroomCarCard car={car} hidePrices={showroomMode} scrollIntoViewAfterRender={ScrollToCarCard} selectedCarId={selectedCarId} setSelectedCarId={setSelectedCarId} equipmentBaseIcons={equipmentIcons} fnAfterUpdate={() => setUpdateData(!updateData)} />
                            </div>
                        ))}
                    </div>

                    <div className="mb-8 mt-8">
                        <CustomerManagerInformationBox title='Need help?' className="shadow bg-white" />
                    </div>

                    <div className="mb-[80px]">
                        <div className='flex items-center justify-center text-xs'>
                            <div className='mr-2 flex items-center'><VATIcon vatStatusId={4} />&nbsp;&nbsp;VAT&nbsp;Included/Margin</div>
                            <div className='mr-2 flex items-center'><VATIcon vatStatusId={0} />&nbsp;&nbsp;VAT&nbsp;Excluded/Net</div>
                        </div>
                    </div>

                    <div className='h-[100px]'></div>
                </div>
            </MobileLayout>
            </>
        )
    } else {
        return (
            <Waiting />
        )
    }
}